import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Send } from "lucide-react";
import { useSimpleQuoteContext } from "@/contexts/SimpleQuoteContext";
import { useQuery } from "@tanstack/react-query";
import { Quote } from "@/types/quote";

export function SimpleTestQuoteSender() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [adminPhoneNumber, setAdminPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingAdmin, setIsFetchingAdmin] = useState(true);
  const [selectedQuoteId, setSelectedQuoteId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const { toast } = useToast();
  
  // Access the SimpleQuoteContext for categories only
  const { categories } = useSimpleQuoteContext();

  // Use our new RPC function for fetching quotes
  const { data: quotesData, isLoading: isLoadingQuotes } = useQuery({
    queryKey: ["test-quotes", selectedCategoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_filtered_quotes', {
          category_filter: selectedCategoryId || null,
          page_size: 10,
          sort_column: 'created_at',
          sort_direction: 'desc'
        });

      if (error) throw error;

      return (data || []).map(quote => ({
        id: quote.id,
        content: quote.content,
        author: quote.author || "",
        source: quote.source || "",
        categories: quote.categories || []
      }));
    }
  });
  
  const quotes = quotesData || [];
  
  // Fetch admin phone number on component mount
  useEffect(() => {
    const fetchAdminPhone = async () => {
      setIsFetchingAdmin(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('phone_number')
            .eq('id', user.id)
            .single();
            
          if (profileData?.phone_number) {
            setAdminPhoneNumber(profileData.phone_number);
          }
        }
      } catch (error) {
        console.error("Error fetching admin phone:", error);
      } finally {
        setIsFetchingAdmin(false);
      }
    };
    
    fetchAdminPhone();
  }, []);

  const handleSendTest = async () => {
    if (!selectedQuoteId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a quote to send"
      });
      return;
    }

    const numberToUse = phoneNumber.trim() || adminPhoneNumber;
    
    if (!numberToUse) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a phone number or set an admin phone number"
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase.functions.invoke('send-quote', {
        body: {
          profileId: user?.id,
          quoteId: selectedQuoteId,
          phoneNumber: numberToUse,
          confirmation: false
        }
      });
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Test quote sent successfully"
      });
    } catch (error) {
      console.error("Error sending test quote:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send test quote"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send Test Quote</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="category-select" className="text-sm font-medium">
            Filter by Category
          </label>
          <Select
            value={selectedCategoryId || "all"}
            onValueChange={(value) => {
              setSelectedCategoryId(value === "all" ? "" : value);
              setSelectedQuoteId(""); // Reset quote selection when category changes
            }}
          >
            <SelectTrigger id="category-select">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label htmlFor="quote-select" className="text-sm font-medium">
            Select Quote <span className="text-xs text-muted-foreground">({quotes?.length || 0} quotes available)</span>
          </label>
          <Select value={selectedQuoteId} onValueChange={setSelectedQuoteId}>
            <SelectTrigger id="quote-select">
              <SelectValue placeholder="Select a quote to send" />
            </SelectTrigger>
            <SelectContent>
              {quotes && quotes.length > 0 ? (
                quotes.map((quote) => (
                  <SelectItem key={quote.id} value={quote.id}>
                    {quote.content.substring(0, 50)}...
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-quotes" disabled>No quotes available</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label htmlFor="phone-number" className="text-sm font-medium flex items-center justify-between">
            <span>Phone Number (optional)</span>
            {adminPhoneNumber && (
              <span className="text-xs text-muted-foreground">
                Admin: {adminPhoneNumber}
              </span>
            )}
          </label>
          <Input
            id="phone-number"
            placeholder={adminPhoneNumber ? 
              "Leave empty to use admin number" : 
              "Enter phone number"
            }
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            If left empty, will use the admin phone number
          </p>
        </div>

        <Button 
          onClick={handleSendTest} 
          disabled={isLoading || !selectedQuoteId || (!phoneNumber && !adminPhoneNumber)}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" /> Send Test Quote
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
